import React, { useEffect, useState, useContext, Suspense, lazy } from 'react';
import { Link } from '@reach/router';
import { I18N, I18NContext, I18NConsumer, GlobalContext, Loading } from 'commons';
import EbCard from 'commons/Card/Card';
import { baseUrl, orgId, setPageTitle, Ajax, cookies } from 'utils';
import MyProfileSection from 'containers/sign-up/review/MyProfileSection';
import { selectShouldShowIncidentSub } from 'state/selectors/subscriptionSelectors';
import * as subscriptionActions from 'state/actions/subscriptionActions';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useCSRFData } from 'commons/Global/api';

const LocationSection = lazy(() => import('containers/sign-up/review/LocationSection'));
const SubscriptionsSection = lazy(() => import('containers/sign-up/review/SubscriptionsSection'));
const AdditionalInformationSection = lazy(() => import('containers/sign-up/review/AdditionalInformationSection'));
const GroupMembershipSection = lazy(() => import('containers/group-membership'));

import 'containers/sign-up/review/review.scss';

const Home = ({ checkShowIncidentSub, shouldIncidentSubShow }) => {
  const [customFieldEditable, setCustomFieldEditable] = useState(false);
  const {
    account: {
      id: contactId,
      ssoCredential, jwtToken,
      hasHipaaBridge, hasSecureBridge, sbWebUri,
      CSRF_TOKEN_FOR_SESSION: csrfToken = '',
      showDirectoryChat
    },
    organization: {
      orgName, showCustomfield, showAddress, showProfile,
      showAlertSubscription, showGroupSubscription, showIncidentSub, groupMemberShipSetting,
      addressSetting: { editable: addresseEditable = false, expectedLocEditable: expLocEditable = false },
    },
    lang,
    csrfData
  } = useContext(GlobalContext);
  const i18n = useContext(I18NContext);
  const shouldShowGroupMembership = groupMemberShipSetting?.exposed;

  const entryFromPublicPortal = cookies.get('entryFromPublicPortal', {
    path: `/${orgId}`
  });

  useEffect(() => {
    if (entryFromPublicPortal) {
      try {
        const storedToken = sessionStorage.getItem('CSRF_TOKEN_FOR_SESSION');
        const newToken = csrfData?.csrfToken;
        if (newToken && !storedToken) {
          Ajax.SET_X_CSRF_TOKEN(newToken);
          sessionStorage.setItem('CSRF_TOKEN_FOR_SESSION', newToken);
        }
      } catch (error) {
        console.error('Error setting CSRF token:', error);
      }
    }
  }, [entryFromPublicPortal, csrfData?.csrfToken]);


  useEffect(() => {
    checkShowIncidentSub({
      contactId,
      csrfToken,
      showAlertSubscription,
      showGroupSubscription,
      showIncidentSub
    });
  }, []);

  const showSubscription = showAlertSubscription || showGroupSubscription || shouldIncidentSubShow;

  return (
    <div className="review container ssp-well">
      <I18NConsumer>
        {() => setPageTitle(`${orgName} - ${i18n['button.home']}`)}
      </I18NConsumer>
      <input type="hidden" name="ssoCredential" id="ssoCredential" value={ssoCredential} />
      <input type="hidden" name="jwtToken" id="jwtToken" value={jwtToken} />
      <h1 className="sr-only">{orgName} - {i18n['button.home']}</h1>
      <div className="row margin10-B pb-2">
        <div className="col-12">
          <h2 className="h1"><I18N>button.home</I18N></h2>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          {showProfile &&
            <section className="form-group" aria-labelledby="profile-section-heading">
              <h3 id="profile-section-heading">
                <Link
                  to="../my-profile"
                  tabIndex="0"
                  eb-test="ssp_home_button_edit_profile"
                >
                  {i18n['ssp.title.profile']} ({i18n['ssp.address.grid.edit']})
                </Link>
              </h3>
              <EbCard showFooter={false}>
                <MyProfileSection isHome />
              </EbCard>
            </section>
          }
          {showAddress &&
            <section className="form-group" aria-labelledby="location-section-heading">
              <h3 id="location-section-heading">
                <Link
                  to="../my-location"
                  tabIndex="0"
                  eb-test="ssp_home_button_edit_mylocation"
                >
                  {i18n['ssp.title.locations']} ({(addresseEditable || expLocEditable) ? i18n['ssp.address.grid.edit'] : i18n['ssp.address.grid.view']})
                </Link>
              </h3>
              <EbCard showFooter={false}>
                <Suspense fallback={<Loading />}>
                  <LocationSection />
                </Suspense>
              </EbCard>
            </section>
          }
          {showCustomfield &&
            <section className="form-group" aria-labelledby="additional-info-section-heading">
              <h3 id="additional-info-section-heading">
                <Link
                  to="../my-additionalInformation"
                  tabIndex="0"
                  eb-test="ssp_home_button_edit_additionalInformation"
                >
                  {i18n['ssp.title.information']} ({customFieldEditable ? i18n['ssp.address.grid.edit'] : i18n['ssp.address.grid.view']})
                </Link>
              </h3>
              <EbCard showFooter={false}>
                <Suspense fallback={<Loading />}>
                  <AdditionalInformationSection setCustomFieldEditable={setCustomFieldEditable} />
                </Suspense>
              </EbCard>
            </section>
          }
        </div>
        <div className="col-md-6">
          {hasHipaaBridge &&
            <div className="well ssp-well hipp p-2">
              <h3 className="mb-0">
                <a href={sbWebUri} target="_blank" rel="noreferrer" className="text-decoration d-flex align-items-center">
                  <span className="hipaabridge_16">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span className="px-2"><I18N>ssp.main.title.hipaabridge</I18N></span>
                </a>
              </h3>
            </div>
          }
          {hasSecureBridge &&
            <div className="well ssp-well secure p-2">
              <h3 className="mb-0">
                <a href={sbWebUri} target="_blank" rel="noreferrer" className="text-decoration d-flex align-items-center">
                  <span className="securebridge_16">&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  <span className="px-2"><I18N>ssp.main.title.securebridge</I18N></span>
                </a>
              </h3>
            </div>
          }
          {showDirectoryChat &&
            <div className="well ssp-well hipp p-2">
              <h3 className="mb-0">
                <a href={`${baseUrl}/${orgId}/chat`} className="text-decoration d-flex align-items-center">
                  <i className="fas fa-video" />
                  <span className="px-2">
                    {(lang === 'en_US' || lang === 'en_GB')
                      ? <>{i18n['secure.chat.title'].toUpperCase()}</>
                      : <I18N>secure.chat.title</I18N>
                    }
                  </span>
                </a>
              </h3>
            </div>
          }
          {showSubscription &&
            <section className="form-group" aria-labelledby="subscriptions-section-heading">
              <h3 id="subscriptions-section-heading">
                <Link
                  to="../my-subscription"
                  tabIndex="0"
                  eb-test="ssp_home_button_edit_subscriptions"
                >
                  {i18n['ssp.main.mySubscriptions']} ({i18n['ssp.address.grid.edit']})
                </Link>
              </h3>
              <EbCard showFooter={false}>
                <Suspense fallback={<Loading />}>
                  <SubscriptionsSection showIncidentSub={shouldIncidentSubShow} />
                </Suspense>

              </EbCard>
            </section>
          }
          {
            shouldShowGroupMembership &&
            <section className="form-group" aria-labelledby="group-membership-section-heading">
              <h3 className="group-membership-section-heading" id="group-membership-section-heading">
                {i18n['ssp.main.groupMembership']}
              </h3>
              <EbCard showFooter={false} itemClass="card-header">
                <Suspense fallback={<Loading />}>
                  <GroupMembershipSection/>
                </Suspense>
              </EbCard>
            </section>
          }
        </div>
      </div>
      <footer aria-label="Footer">
        <h2 className="sr-only">Footer</h2>
        {/* Footer content */}
      </footer>
    </div>
  );
};

Home.propTypes = {
  checkShowIncidentSub: PropTypes.func.isRequired,
  shouldIncidentSubShow: PropTypes.bool.isRequired,
};

const mapDispatchToProps = dispatch => ({
  checkShowIncidentSub: data => dispatch(subscriptionActions.checkShowIncidentSub.action(data))
});

const mapStateToProps = state => ({
  shouldIncidentSubShow: selectShouldShowIncidentSub(state),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Home);
